import {createContext, ReactNode, useCallback, useMemo, useState} from 'react';

import {PartialDeep} from 'type-fest';

import Authentication from './Authentication';
import {IUser} from './models';
import AWSAuthProvider from './services/aws/AWSAuthProvider';
import JwtAuthProvider from './services/jwt/JwtAuthProvider';

export type AuthContextType = {
  updateUser?: (U: PartialDeep<IUser>) => void;
  isAuthenticated: boolean;
  setIsAuthenticated: (T: boolean) => void;
  setAuthProvider: (T: string) => void;
  getAuthProvider: () => string | null;
  resetAuthProvider: () => string | null;
};

export const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  setIsAuthenticated: () => {},
  setAuthProvider: () => {},
  getAuthProvider: () => null,
  resetAuthProvider: () => null,
});

const authProviderLocalStorageKey = 'fuseReactAuthProvider';

type AuthenticationProviderProps = {children: ReactNode};

function AuthenticationProvider({children}: AuthenticationProviderProps) {
  // const {children} = props;
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  /**
   * Get auth provider
   */
  const getAuthProvider = useCallback(() => {
    const auth = localStorage.getItem(authProviderLocalStorageKey);
    return auth;
  }, []);

  /**
   * Set auth provider
   */
  const setAuthProvider = useCallback((authProvider: string) => {
    if (authProvider) {
      localStorage.setItem(authProviderLocalStorageKey, authProvider);
    }
  }, []);

  /**
   * Remove auth provider
   */
  const resetAuthProvider = useCallback(() => {
    localStorage.removeItem(authProviderLocalStorageKey);
  }, []);

  const contextValue = useMemo(
    () =>
      ({
        setIsAuthenticated,
        isAuthenticated,
        getAuthProvider,
        setAuthProvider,
        resetAuthProvider,
      }) as AuthContextType,
    [
      getAuthProvider,
      setAuthProvider,
      resetAuthProvider,
      setIsAuthenticated,
      isAuthenticated,
    ],
  );

  return (
    <AuthContext.Provider value={contextValue}>
      <JwtAuthProvider>
        <AWSAuthProvider>
          <Authentication>{children}</Authentication>
        </AWSAuthProvider>
      </JwtAuthProvider>
    </AuthContext.Provider>
  );
}

export default AuthenticationProvider;
